import { atom, useAtom } from '@metorial/data-hooks';
import { Avatar, Button, Spacer, theme } from '@metorial/ui';
import * as Popover from '@radix-ui/react-popover';
import { RiArrowDownSLine, RiArrowLeftSLine } from '@remixicon/react';
import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { appLayoutSidebarStateAtom } from './atom';

// BlackOS application shell, laid out like the cloud dashboard:
//
//   ┌──────┬──────────────────────────────────────────────────────┐
//   │ rail │ top bar: org › project/portal · search ⌘K · help · + │
//   │      ├────────────┬─────────────────────────────────────────┤
//   │ icon │  section   │                                         │
//   │  +   │  sub-nav   │             content panel               │
//   │label │ (groups ▾) │                                         │
//   └──────┴────────────┴─────────────────────────────────────────┘
//
// The rail and the sub-nav are plain data; the app decides what is active.

export interface RailItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  to?: string;
  onClick?: () => void;
  active?: boolean;
}

export interface SubNavLink {
  label: React.ReactNode;
  to: string;
  active?: boolean;
  icon?: React.ReactNode;
  /** Shown indented below the item while it (or one of them) is active. */
  children?: SubNavLink[];
  /** Opens in a new tab. */
  external?: boolean;
}

export interface SubNavGroup {
  /** Collapsible header (e.g. "Catalog"). Groups without a label are always open. */
  label?: string;
  items: SubNavLink[];
}

export interface SubNavSpec {
  /** Used to remember which groups are collapsed. */
  key: string;
  header?: React.ReactNode;
  groups: SubNavGroup[];
  footer?: React.ReactNode;
}

export let RAIL_WIDTH = 80;
export let SUBNAV_WIDTH = 228;
export let TOP_BAR_HEIGHT = 56;

let GlobalStyle = createGlobalStyle`
  body {
    background: ${theme.colors.gray300};
  }
`;

let Root = styled.div`
  height: 100dvh;
  display: grid;
  grid-template-columns: ${RAIL_WIDTH}px minmax(0, 1fr);
  background: ${theme.colors.gray300};
`;

let RailWrapper = styled.nav`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 0 12px;
  gap: 2px;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
`;

let RailLogo = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-bottom: 10px;
  border-radius: 12px;

  &:hover,
  &:focus-visible {
    background: ${theme.colors.gray400};
  }
`;

let RailButtonInner = styled.span`
  width: 74px;
  padding: 5px 0 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  border-radius: 10px;
  color: ${theme.colors.gray700};
  cursor: pointer;

  &[data-active='true'] {
    color: ${theme.colors.foreground};
  }
`;

let RailIcon = styled.span`
  width: 38px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  border: 1px solid transparent;

  svg {
    width: 19px;
    height: 19px;
  }

  ${RailButtonInner}:hover &,
  a:focus-visible &,
  button:focus-visible & {
    background: ${theme.colors.gray400};
  }

  /* Active: a plain grey rounded square, like the cloud. */
  ${RailButtonInner}[data-active='true'] & {
    background: ${theme.colors.gray400};
  }
`;

let RailLabel = styled.span`
  font-size: 11px;
  line-height: 14px;
  font-weight: 500;
  text-align: center;
  max-width: 74px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

let RailPlainButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  outline: none;
`;

let RailSpacer = styled.div`
  flex-grow: 1;
  min-height: 12px;
`;

let Main = styled.div`
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: ${TOP_BAR_HEIGHT}px minmax(0, 1fr);
`;

// The side slots never shrink below their content (the workspace pill and the
// settings selectors stay readable); the search box in the middle gives way.
let TopBarWrapper = styled.header`
  display: grid;
  grid-template-columns: minmax(max-content, 1fr) minmax(160px, 460px) minmax(max-content, 1fr);
  align-items: center;
  gap: 14px;
  padding: 0 12px 0 0;
  min-width: 0;

  &[data-center='false'] {
    grid-template-columns: minmax(max-content, 1fr) minmax(0, auto);
  }
`;

let TopBarSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  &[data-align='end'] {
    justify-content: flex-end;
  }

  &[data-align='center'] {
    justify-content: center;
  }
`;

let Body = styled.div`
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  padding: 0 10px 10px 0;
  gap: 10px;

  &[data-subnav='true'] {
    grid-template-columns: ${SUBNAV_WIDTH}px minmax(0, 1fr);
  }

  @media (max-width: 860px) {
    &[data-subnav='true'] {
      grid-template-columns: minmax(0, 1fr);
    }
  }
`;

let SubNavWrapper = styled.aside`
  min-height: 0;
  overflow-y: auto;
  padding: 2px 2px 16px 0;
  display: flex;
  flex-direction: column;
  scrollbar-width: thin;

  @media (max-width: 860px) {
    display: none;
  }
`;

let SubNavHeader = styled.div`
  padding: 4px 8px 12px;
`;

let SubNavGroupWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  &:not(:last-child) {
    margin-bottom: 14px;
  }
`;

let SubNavGroupLabel = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  padding: 3px 8px;
  margin-bottom: 2px;
  border: none;
  border-radius: 6px;
  background: none;
  color: ${theme.colors.gray650};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${theme.colors.gray400};
    color: ${theme.colors.foreground};
  }

  /* BlackOS: no motion; the chevron flips instantly. */
  &[data-collapsed='true'] svg {
    transform: rotate(-90deg);
  }
`;

let SubNavItem = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 32px;
  padding: 0 10px;
  border-radius: 7px;
  font-size: 14px;
  font-weight: 450;
  color: ${theme.colors.gray700};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &:hover {
    background: ${theme.colors.gray400};
  }

  &[data-active='true'] {
    background: ${theme.colors.gray400};
    color: ${theme.colors.foreground};
    font-weight: 600;
  }

  &[data-active='true']::after {
    content: '';
    position: absolute;
    left: -2px;
    top: 7px;
    bottom: 7px;
    width: 4px;
    border-radius: 4px;
    background: ${theme.colors.primary};
  }

  &[data-child='true'] {
    margin-left: 14px;
    height: 30px;
    font-size: 13px;
  }
`;

let ContentPanel = styled.main`
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow: auto;
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.gray400};
  border-radius: 10px;
  box-shadow: ${theme.shadows.medium};
  position: relative;
`;

let collapsedGroupsAtom = atom<string[]>([]);

let SubNavLinkRow = ({ link, child }: { link: SubNavLink; child?: boolean }) => {
  let inner = (
    <SubNavItem data-active={!!link.active} data-child={!!child}>
      {link.icon}
      {link.label}
    </SubNavItem>
  );

  if (link.external) {
    return (
      <a href={link.to} target="_blank" rel="noreferrer">
        {inner}
      </a>
    );
  }

  return <Link to={link.to}>{inner}</Link>;
};

export let SubNav = ({ spec }: { spec: SubNavSpec }) => {
  let collapsed = useAtom(collapsedGroupsAtom);

  return (
    <SubNavWrapper aria-label="Section navigation">
      {spec.header && <SubNavHeader>{spec.header}</SubNavHeader>}

      {spec.groups.map((group, index) => {
        let key = `${spec.key}:${group.label ?? index}`;
        let isCollapsed = !!group.label && collapsed.includes(key);

        return (
          <SubNavGroupWrapper key={key}>
            {group.label && (
              <SubNavGroupLabel
                type="button"
                data-collapsed={isCollapsed}
                aria-expanded={!isCollapsed}
                onClick={() =>
                  collapsedGroupsAtom.set(
                    isCollapsed ? collapsed.filter(k => k != key) : [...collapsed, key]
                  )
                }
              >
                {group.label}
                <RiArrowDownSLine size={14} />
              </SubNavGroupLabel>
            )}

            {!isCollapsed &&
              group.items.map((item, itemIndex) => {
                let childActive = item.children?.some(c => c.active);

                return (
                  <Fragment key={itemIndex}>
                    <SubNavLinkRow link={{ ...item, active: item.active && !childActive }} />

                    {(item.active || childActive) &&
                      item.children?.map((c, childIndex) => (
                        <SubNavLinkRow key={childIndex} link={c} child />
                      ))}
                  </Fragment>
                );
              })}
          </SubNavGroupWrapper>
        );
      })}

      {spec.footer && (
        <>
          <RailSpacer />
          {spec.footer}
        </>
      )}
    </SubNavWrapper>
  );
};

let RailEntry = ({ item }: { item: RailItem }) => {
  let inner = (
    <RailButtonInner data-active={!!item.active}>
      <RailIcon>{item.icon}</RailIcon>
      <RailLabel>{item.label}</RailLabel>
    </RailButtonInner>
  );

  if (item.to) {
    return (
      <Link
        to={item.to}
        onClick={item.onClick}
        aria-label={item.label}
        aria-current={item.active ? 'page' : undefined}
      >
        {inner}
      </Link>
    );
  }

  return (
    <RailPlainButton type="button" onClick={item.onClick} aria-label={item.label}>
      {inner}
    </RailPlainButton>
  );
};

let AccountContent = styled(Popover.Content)`
  z-index: 999;
  width: 300px;
  background: rgba(250, 250, 250, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid ${theme.colors.gray400};
  border-radius: 14px;
  box-shadow: ${theme.shadows.large};
  overflow: hidden;
`;

let AccountHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid ${theme.colors.gray400};
`;

let AccountName = styled.p`
  font-size: 15px;
  font-weight: 600;
  color: ${theme.colors.gray900};
`;

let AccountEmail = styled.p`
  font-size: 13px;
  color: ${theme.colors.gray700};
  overflow: hidden;
  text-overflow: ellipsis;
`;

let AccountActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px 16px;
`;

/** The avatar at the bottom of the rail; opens the account menu (Account, Logout). */
export let RailAccountMenu = ({
  user,
  accountHref,
  onLogout,
  active
}: {
  user:
    | {
        name: string;
        email?: string | null;
        photoUrl?: string | null;
        imageUrl?: string | null;
      }
    | null
    | undefined;
  accountHref: string;
  onLogout: () => void;
  active?: boolean;
}) => (
  <Popover.Root>
    <Popover.Trigger asChild>
      <RailPlainButton type="button" aria-label="Open account menu">
        <RailButtonInner data-active={!!active}>
          <RailIcon style={{ borderRadius: 999, width: 32, height: 32 }}>
            <Avatar entity={user ?? null} size={28} noTooltip />
          </RailIcon>
          <RailLabel>Account</RailLabel>
        </RailButtonInner>
      </RailPlainButton>
    </Popover.Trigger>

    <Popover.Portal>
      <AccountContent side="right" align="end" sideOffset={8}>
        <AccountHeader>
          <Avatar entity={user ?? null} size={44} noTooltip />
          <div style={{ minWidth: 0 }}>
            <AccountName>{user?.name}</AccountName>
            <Spacer size={2} />
            <AccountEmail>{user?.email}</AccountEmail>
          </div>
        </AccountHeader>

        <AccountActions>
          <Link to={accountHref}>
            <Button size="2" fullWidth variant="solid" as="span">
              Account
            </Button>
          </Link>

          <Button size="2" fullWidth variant="outline" onClick={onLogout}>
            Logout
          </Button>
        </AccountActions>
      </AccountContent>
    </Popover.Portal>
  </Popover.Root>
);

/** "‹ Back to …" link for sub-nav headers (settings and account areas). */
export let SubNavBackLink = ({ to, label }: { to: string; label: React.ReactNode }) => (
  <Link to={to}>
    <SubNavItem style={{ paddingLeft: 4, gap: 4, color: theme.colors.gray700 }}>
      <RiArrowLeftSLine />
      {label}
    </SubNavItem>
  </Link>
);

export let SubNavTitle = styled.p`
  font-size: 13px;
  font-weight: 650;
  color: ${theme.colors.gray900};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** Round icon button for the top bar (help, create). */
export let TopBarIconButton = styled.button`
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid transparent;
  background: none;
  color: ${theme.colors.gray700};
  cursor: pointer;
  padding: 0;

  svg {
    width: 19px;
    height: 19px;
  }

  &:hover,
  &:focus-visible,
  &[data-state='open'] {
    background: ${theme.colors.gray400};
    color: ${theme.colors.foreground};
  }

  &[data-variant='primary'] {
    background: ${theme.colors.primary};
    color: white;

    &:hover,
    &:focus-visible,
    &[data-state='open'] {
      background: ${theme.colors.primary600};
      color: white;
    }
  }
`;

export let RailLayout = ({
  logo,
  logoHref,
  items,
  bottomItems,
  account,
  topBar,
  subNav,
  children
}: {
  logo: React.ReactNode;
  logoHref: string;
  items: RailItem[];
  bottomItems?: RailItem[];
  account?: React.ReactNode;
  topBar: { left?: React.ReactNode; center?: React.ReactNode; right?: React.ReactNode };
  subNav?: SubNavSpec | null;
  children: React.ReactNode;
}) => {
  // Pages that check whether the (old) sidebar is collapsed see an open one.
  React.useEffect(() => {
    appLayoutSidebarStateAtom.set({ layoutId: 'rail', collapsed: !subNav });
  }, [!subNav]);

  return (
    <Root>
      <GlobalStyle />

      <RailWrapper aria-label="Main navigation">
        <RailLogo to={logoHref} aria-label="Home">
          {logo}
        </RailLogo>

        {items.map(item => (
          <RailEntry key={item.id} item={item} />
        ))}

        <RailSpacer />

        {bottomItems?.map(item => (
          <RailEntry key={item.id} item={item} />
        ))}

        {account}
      </RailWrapper>

      <Main>
        <TopBarWrapper data-center={!!topBar.center}>
          <TopBarSlot>{topBar.left}</TopBarSlot>
          {topBar.center && <TopBarSlot data-align="center">{topBar.center}</TopBarSlot>}
          <TopBarSlot data-align="end">{topBar.right}</TopBarSlot>
        </TopBarWrapper>

        <Body data-subnav={!!subNav}>
          {subNav && <SubNav spec={subNav} />}

          <ContentPanel>{children}</ContentPanel>
        </Body>
      </Main>
    </Root>
  );
};
